export function catalogTitle(row = {}) {
  const raw = String(row.displayName || row.display_name || row.project || row.xerName || '')
    .replace(/\.(xlsx|xls|xer)$/i, '')
    .trim();
  return raw || String(row.project || '').trim() || 'Untitled';
}

export function catalogSubtitle(row = {}) {
  const bits = [];
  if (row.clientName) bits.push(row.clientName);
  if (row.estimateName && row.estimateName !== row.clientName) bits.push(row.estimateName);
  if (row.estimateCode) bits.push(row.estimateCode);
  if (row.jobNumber) bits.push(`Job ${row.jobNumber}`);
  if (row.source === 'p6' || row.p6ProjId) {
    if (row.xerClass === 'current') bits.push('P6 Current');
    else if (row.submitDate) bits.push(`P6 ${row.submitDate}`);
    else if (row.xerClass === 'proposal') bits.push('P6 proposal');
    else if (row.xerClass === 'scenario') bits.push('P6 scenario');
    else bits.push('P6 XER');
  }
  return bits.join(' · ');
}

export function compactSearch(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export function catalogSearchText(row = {}) {
  const p6 = row.source === 'p6' || row.p6ProjId;
  const text = [
    catalogTitle(row),
    row.project,
    row.jobNumber,
    row.estimateCode,
    row.estimateName,
    row.clientName,
    row.xerClass,
    row.submitDate,
    row.p6ProjId,
    p6 ? null : String(row.fileName || '').replace(/\.(xlsx|xls|xer)$/i, ''),
  ].filter(Boolean).join(' ').toLowerCase();
  const compact = compactSearch(text);
  return compact && compact !== text.replace(/\s+/g, '') ? `${text} ${compact}` : text;
}

export function catalogRowKey(row = {}) {
  return [row.p6ProjId || '', row.project || '', row.jobNumber || '', row.estimateCode || ''].join('|');
}

function normEstimate(value) {
  return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Key for a saved program member that is not on the current XER list. */
export function orphanMemberKey(member = {}) {
  return `saved|${member.projectKey || ''}|${member.jobNumber || ''}|${normEstimate(member.estimateCode)}`;
}

export function findCatalogRowForMember(member = {}, catalog = []) {
  const project = String(member.projectKey || '').trim();
  const job = String(member.jobNumber || '').trim();
  const est = normEstimate(member.estimateCode);
  const rows = catalog || [];
  const strict = rows.find((c) => (
    String(c.project || '') === project
    && String(c.jobNumber || '') === job
    && normEstimate(c.estimateCode) === est
  ));
  if (strict) return strict;
  if (project) {
    const byProject = rows.find((c) => String(c.project || '') === project);
    if (byProject) return byProject;
  }
  if (est) {
    const byEst = rows.find((c) => normEstimate(c.estimateCode) === est);
    if (byEst) return byEst;
  }
  if (job) {
    const byJob = rows.find((c) => String(c.jobNumber || '') === job);
    if (byJob) return byJob;
  }
  return null;
}

/** Map saved program members onto catalog checkbox keys, keeping any that are not in the list. */
export function resolveProgramMembers(members, catalog) {
  const keys = new Set();
  const orphans = [];
  (members || []).forEach((member) => {
    const match = findCatalogRowForMember(member, catalog);
    if (match) {
      keys.add(catalogRowKey(match));
      return;
    }
    const key = orphanMemberKey(member);
    keys.add(key);
    orphans.push({
      key,
      member,
      title: member.projectKey || 'Saved project',
      subtitle: [member.estimateCode, member.jobNumber ? `Job ${member.jobNumber}` : '', 'Not in the current XER list']
        .filter(Boolean)
        .join(' · '),
    });
  });
  return { keys, orphans };
}

export function suggestProgramsFromCatalog(catalog) {
  const byClient = new Map();
  (catalog || []).forEach((row) => {
    const clientName = String(row.clientName || '').trim();
    if (!clientName) return;
    if (!byClient.has(clientName)) byClient.set(clientName, []);
    byClient.get(clientName).push(row);
  });
  return [...byClient.entries()]
    .map(([clientName, rows]) => ({
      clientName,
      layer: 'program',
      members: rows.map((r) => ({
        projectKey: r.project,
        jobNumber: r.jobNumber || null,
        estimateCode: r.estimateCode || null,
      })),
    }))
    .filter((s) => s.members.length)
    .sort((a, b) => a.clientName.localeCompare(b.clientName));
}

export function catalogMatchesSearch(row, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const text = catalogSearchText(row);
  if (text.includes(q)) return true;
  const compactQ = compactSearch(q);
  return compactQ.length >= 3 && compactSearch(text).includes(compactQ);
}

export function formatEstimateCode(code) {
  const raw = String(code || '').trim();
  if (!raw) return null;
  const compact = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const m = compact.match(/^(\d{2})([A-Z]{2})(\d{4})$/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : raw;
}

export function xerCopyLabel(job = {}) {
  if (job.xerClass === 'current') return 'P6 Current';
  if (job.submitDate) return `P6 ${job.submitDate}`;
  if (job.xerClass === 'dated') return 'P6 dated weekly';
  if (job.xerClass === 'proposal') return 'P6 proposal';
  if (job.xerClass === 'scenario') return 'P6 scenario';
  if (job.p6ProjId || job.xerClass) return 'P6 XER';
  return null;
}

const HOVER_JOB_CAP = 8;

export function legendHoverLines(meta = {}, seriesName = '') {
  const title = String(meta.label || seriesName || '')
    .replace(/\.(xlsx|xls)$/i, '')
    .replace(/\s*[-–—]\s*data\s*dump\s*$/i, '')
    .trim();
  const allJobs = Array.isArray(meta.jobs) ? meta.jobs.filter(Boolean) : [];
  const extra = Math.max(0, allJobs.length - HOVER_JOB_CAP);
  const jobs = allJobs.slice(0, HOVER_JOB_CAP);
  const lines = [];
  if (title) lines.push({ kind: 'title', text: title });
  const multi = jobs.length > 1;
  jobs.forEach((job, i) => {
    if (multi && i > 0) lines.push({ kind: 'rule', text: '' });
    const name = String(job.estimateName || '').trim() || null;
    const client = String(job.clientName || '').trim() || null;
    if (name) lines.push({ kind: 'name', text: name });
    if (client && client !== name) lines.push({ kind: 'field', label: 'Client', text: client });
    const est = formatEstimateCode(job.estimateCode);
    if (est) lines.push({ kind: 'field', label: 'Estimate', text: est });
    if (job.jobNumber) lines.push({ kind: 'field', label: 'Job', text: String(job.jobNumber) });
    const xer = xerCopyLabel(job);
    if (xer) lines.push({ kind: 'field', label: 'XER', text: xer });
    if (multi && job.xerName && job.xerName !== title) {
      lines.push({ kind: 'field', label: 'P6 name', text: job.xerName });
    }
  });
  if (extra) lines.push({ kind: 'muted', text: `+${extra} more` });
  if (lines.length <= 1) {
    lines.push({ kind: 'muted', text: 'No job or estimate on this file yet' });
  }
  return lines;
}
